import React, { useCallback, useMemo, useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { InputNumber } from 'primereact/inputnumber';

// Bipolar "size the category up/down" control. Works internally in integer
// percentage delta vs. last quarter (0% = baseline = 100% of last quarter).
// The bounds map to a scale factor (1 + delta/100) that stays within the
// backend-accepted band (gt 0, le 5.0).
const MIN = -95;
const MAX = 100;
const RANGE = MAX - MIN; // 195
const BASE_PCT = ((0 - MIN) / RANGE) * 100; // 48.72% — where 0% sits on the track

const DEFAULT_FACTOR = 1.0;

interface CategorySizeControlProps {
  /** Category this control applies to (used only for labelling). */
  category: string;
  /** Current scale factor (1.0 = 100% of last quarter). */
  factor: number;
  /** Called with the new scale factor whenever the user changes the size. */
  onChange: (factor: number) => void;
  /**
   * Last quarter's actual category units for this category (the "Current" total).
   * When provided, the caption shows the modeled unit count instead of a bare %.
   */
  baselineUnits?: number | null;
  disabled?: boolean;
}

const clampDelta = (n: number): number => Math.max(MIN, Math.min(MAX, Math.round(n)));

// Keep the emitted factor within the backend-accepted band (gt 0, le 5.0).
const clampFactor = (factor: number): number => Math.min(5, Math.max(0.05, factor));

const factorToDelta = (factor: number | undefined): number =>
  clampDelta(Math.round(((factor ?? DEFAULT_FACTOR) - 1) * 100));

const deltaToFactor = (delta: number): number =>
  clampFactor(1 + clampDelta(delta) / 100);

const fmt = (v: number): string => `${v > 0 ? '+' : ''}${v}%`;

/**
 * Per-category control that lets the user model a larger or smaller category than
 * last quarter's actual buy. Emits a scale factor (1.0 = no change) consumed by the
 * scenario generation request. Presentational only — all state lives in the parent.
 */
const CategorySizeControl: React.FC<CategorySizeControlProps> = ({
  category,
  factor,
  onChange,
  baselineUnits = null,
  disabled = false,
}) => {
  const value = useMemo(() => factorToDelta(factor), [factor]);
  // Modeled units = last quarter's actual units scaled by the displayed % change.
  const modeledUnits = useMemo(
    () => (baselineUnits != null ? Math.round(baselineUnits * (1 + value / 100)) : null),
    [baselineUnits, value]
  );
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const commit = useCallback(
    (v: number) => {
      if (disabled) return;
      onChange(deltaToFactor(v));
    },
    [disabled, onChange]
  );

  // derived geometry
  const pct = ((value - MIN) / RANGE) * 100; // handle position, %
  const grow = value > 0;
  const shrink = value < 0;
  const fillLeft = shrink ? pct : BASE_PCT;
  const fillWidth = Math.abs(pct - BASE_PCT);
  const accent = grow ? '#16a34a' : shrink ? '#b91c1c' : '#94a3b8';

  // drag handling
  const posToValue = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return value;
    const r = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return MIN + ratio * RANGE;
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    commit(posToValue(e.clientX));
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging.current) commit(posToValue(e.clientX));
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  const boxStyle: React.CSSProperties = { ...S.box, opacity: disabled ? 0.6 : 1 };
  const infoClass = `category-size-info-${category.replace(/[^a-zA-Z0-9]/g, '')}`;
  const resetClass = `category-size-reset-${category.replace(/[^a-zA-Z0-9]/g, '')}`;
  const inputId = `category-size-input-${category.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div className="category-size-control" style={boxStyle}>
      <Tooltip target={`.${infoClass}`} position="top" />
      <Tooltip target={`.${resetClass}`} position="top" />

      {/* header band — label + inline size input */}
      <div style={S.head}>
        <div style={S.label}>
          Category Size
          <span
            className={infoClass}
            style={S.info}
            data-pr-tooltip="Model this category at a larger or smaller volume than last quarter's actual category units. 0% = same as last quarter's actual units."
            data-pr-position="top"
          >
            i
          </span>
        </div>
        <div style={S.headControls}>
          <InputNumber
            inputId={inputId}
            value={value}
            onValueChange={(e) => commit(typeof e.value === 'number' ? e.value : 0)}
            min={MIN}
            max={MAX}
            step={1}
            suffix="%"
            useGrouping={false}
            minFractionDigits={0}
            maxFractionDigits={0}
            disabled={disabled}
            inputMode="numeric"
            placeholder="e.g. +10%"
            inputStyle={{ ...S.value, width: '100%', borderColor: accent, color: accent }}
            style={{ width: 96 }}
            aria-label={`Category size percent for ${category}`}
          />
          <span
            className={resetClass}
            data-pr-tooltip="Reset to baseline"
            data-pr-position="top"
            style={{ display: 'inline-flex' }}
          >
            <button
              type="button"
              style={{ ...S.reset, opacity: value === 0 ? 0.4 : 1 }}
              onClick={() => commit(0)}
              disabled={disabled || value === 0}
              aria-label={`Reset ${category} size to baseline`}
            >
              ↺
            </button>
          </span>
        </div>
      </div>

      {/* slider section */}
      <div style={S.section}>
        <div
          ref={trackRef}
          style={{ ...S.track, cursor: disabled ? 'not-allowed' : 'pointer' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div style={S.rail} />
          <div style={{ ...S.fill, left: `${fillLeft}%`, width: `${fillWidth}%`, background: accent }} />
          <span style={{ ...S.tick, left: 0 }} />
          <span style={{ ...S.tick, left: `${BASE_PCT}%`, background: '#94a3b8' }} />
          <span style={{ ...S.tick, right: 0 }} />
          <div
            role="slider"
            aria-label={`Category size for ${category}`}
            aria-valuemin={MIN}
            aria-valuemax={MAX}
            aria-valuenow={value}
            aria-valuetext={fmt(value)}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                e.preventDefault();
                commit(value + 1);
              }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                e.preventDefault();
                commit(value - 1);
              }
            }}
            style={{ ...S.handle, left: `${pct}%`, cursor: disabled ? 'not-allowed' : 'grab' }}
          />
        </div>

        <div style={S.axis}>
          <span style={{ color: '#b91c1c' }}>◀ Shrink</span>
          <span style={{ color: '#94a3b8' }}>Baseline · 0%</span>
          <span style={{ color: '#15803d' }}>Grow ▶</span>
        </div>
        <div style={S.minmax}>
          <span>−95%</span>
          <span>+100%</span>
        </div>
      </div>

      <p style={S.caption}>
        {baselineUnits != null && modeledUnits != null ? (
          value === 0 ? (
            <>
              Modeling <strong style={S.captionStrong}>{baselineUnits.toLocaleString()}</strong> category units
              (baseline).
            </>
          ) : (
            <>
              Modeling <strong style={S.captionStrong}>{modeledUnits.toLocaleString()}</strong> category units{' '}
              <span style={{ color: accent, fontWeight: 700 }}>({fmt(value)})</span>.
            </>
          )
        ) : value === 0 ? (
          <>At baseline — last quarter&apos;s actual category units.</>
        ) : (
          <>
            Modeling <strong style={S.captionStrong}>{fmt(value)}</strong> of last quarter&apos;s category units.
          </>
        )}
      </p>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  box: { fontFamily: 'inherit', userSelect: 'none', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', background: '#eef2f7', borderBottom: '1px solid #e2e8f0' },
  label: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600, color: '#334155' },
  info: { width: 15, height: 15, borderRadius: '50%', border: '1.4px solid #b6c1d1', color: '#97a4b8', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  headControls: { display: 'flex', alignItems: 'center', gap: 6 },
  section: { padding: '18px 14px 12px' },
  track: { position: 'relative', height: 40, margin: '0 2px', touchAction: 'none' },
  rail: { position: 'absolute', top: 17, left: 0, right: 0, height: 6, borderRadius: 999, background: '#e2e8f0' },
  fill: { position: 'absolute', top: 17, height: 6, borderRadius: 999 },
  tick: { position: 'absolute', top: 12, width: 2, height: 16, background: '#cbd5e1' },
  handle: { position: 'absolute', top: 8, transform: 'translateX(-50%)', width: 24, height: 24, borderRadius: '50%', background: '#fff', border: '3px solid #1b3a63', boxShadow: '0 3px 8px rgba(20,44,76,.22)', outline: 'none' },
  axis: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 2px 0', fontSize: 11, fontWeight: 600 },
  minmax: { display: 'flex', justifyContent: 'space-between', margin: '3px 2px 0', fontSize: 10, color: '#97a4b8', fontWeight: 600 },
  value: { height: 34, border: '1.5px solid #1b3a63', borderRadius: 8, textAlign: 'center', fontSize: 14, fontWeight: 700, background: '#fff', padding: '0 6px' },
  reset: { width: 34, height: 34, borderRadius: 8, border: 'none', background: 'transparent', color: '#64748b', fontSize: 16, cursor: 'pointer' },
  caption: { margin: 0, padding: '10px 12px 12px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 },
  captionStrong: { color: '#1f2b3d', fontWeight: 700 },
};

export default CategorySizeControl;
